import React from 'react';
import { 
  XIcon, 
  PrinterIcon, 
  DownloadIcon,
  CreditCardIcon,
  CalendarIcon,
  UserIcon,
  FileTextIcon,
  CheckCircleIcon,
  ClockIcon,
  AlertTriangleIcon,
  AlertCircleIcon
} from 'lucide-react';

interface BillItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

interface Patient {
  id: string;
  patientNumber: string;
  user: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
}

interface Doctor {
  user: {
    firstName: string;
    lastName: string;
  };
}

interface Appointment {
  id: string;
  doctor: Doctor;
}

interface Billing {
  id: string;
  amount: number;
  tax: number;
  discount: number;
  totalAmount: number;
  paymentStatus: 'PENDING' | 'PAID' | 'PARTIAL' | 'OVERDUE' | 'CANCELLED';
  paymentMethod?: string;
  transactionId?: string;
  dueDate: string;
  paidDate?: string;
  createdAt: string;
  patient: Patient;
  appointment?: Appointment;
  billItems: BillItem[];
}

interface BillingDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  billing: Billing | null;
  onPayment?: () => void;
}

const BillingDetailsModal: React.FC<BillingDetailsModalProps> = ({
  isOpen,
  onClose,
  billing,
  onPayment
}) => {
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid': return 'badge-success';
      case 'partial': return 'badge-warning';
      case 'pending': return 'badge-secondary';
      case 'overdue': return 'badge-danger';
      case 'cancelled': return 'badge-secondary';
      default: return 'badge-secondary';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid': return <CheckCircleIcon className="h-4 w-4" />;
      case 'partial': return <ClockIcon className="h-4 w-4" />;
      case 'pending': return <ClockIcon className="h-4 w-4" />;
      case 'overdue': return <AlertTriangleIcon className="h-4 w-4" />;
      case 'cancelled': return <AlertCircleIcon className="h-4 w-4" />;
      default: return <ClockIcon className="h-4 w-4" />;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    // TODO: Implement PDF download
    console.log('Download invoice:', billing?.id);
  };

  if (!isOpen || !billing) return null;

  const isOverdue = new Date(billing.dueDate) < new Date() && billing.paymentStatus !== 'PAID';
  const canPayment = billing.paymentStatus !== 'PAID' && billing.paymentStatus !== 'CANCELLED';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <FileTextIcon className="h-6 w-6 text-blue-600 mr-3" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Invoice Details</h2>
              <p className="text-sm text-gray-600">Invoice #{billing.id.substring(0, 8)}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handlePrint}
              className="btn-secondary text-sm"
              title="Print Invoice"
            >
              <PrinterIcon className="h-4 w-4 mr-1" />
              Print
            </button>
            <button
              onClick={handleDownload}
              className="btn-secondary text-sm"
              title="Download PDF"
            >
              <DownloadIcon className="h-4 w-4 mr-1" />
              Download
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <XIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)] p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-6">
              {/* Patient Information */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                  <UserIcon className="h-5 w-5 mr-2" />
                  Patient Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Name</p>
                    <p className="font-medium text-gray-900">
                      {billing.patient.user.firstName} {billing.patient.user.lastName}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Patient Number</p>
                    <p className="font-medium text-gray-900">{billing.patient.patientNumber}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-medium text-gray-900">{billing.patient.user.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Phone</p>
                    <p className="font-medium text-gray-900">{billing.patient.user.phone || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Appointment Information */}
              {billing.appointment && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                    <CalendarIcon className="h-5 w-5 mr-2" />
                    Related Appointment
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Doctor</p>
                      <p className="font-medium text-gray-900">
                        Dr. {billing.appointment.doctor.user.firstName} {billing.appointment.doctor.user.lastName}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Appointment ID</p>
                      <p className="font-medium text-gray-900">
                        {billing.appointment.id.substring(0, 8)}...
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Billing Items */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Services & Items</h3>
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Qty
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Unit Price
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {billing.billItems.map((item) => (
                        <tr key={item.id}>
                          <td className="px-4 py-3 text-sm text-gray-900">{item.description}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-center">{item.quantity}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right">
                            {formatCurrency(Number(item.unitPrice))}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                            {formatCurrency(Number(item.totalPrice))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Invoice Totals */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Invoice Summary</h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="text-gray-900">{formatCurrency(Number(billing.amount))}</span>
                  </div>
                  {Number(billing.tax) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Tax:</span>
                      <span className="text-gray-900">{formatCurrency(Number(billing.tax))}</span>
                    </div>
                  )}
                  {Number(billing.discount) > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Discount:</span>
                      <span>-{formatCurrency(Number(billing.discount))}</span>
                    </div>
                  )}
                  <div className="border-t border-gray-200 pt-3 flex justify-between text-lg font-medium">
                    <span className="text-gray-900">Total Amount:</span>
                    <span className="text-gray-900">{formatCurrency(Number(billing.totalAmount))}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Payment Status */}
              <div className="bg-white border border-gray-200 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Payment Status</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Status:</span>
                    <span className={`badge ${getStatusColor(billing.paymentStatus)} flex items-center`}>
                      {getStatusIcon(billing.paymentStatus)}
                      <span className="ml-1 capitalize">{billing.paymentStatus.toLowerCase()}</span>
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Due Date:</span>
                    <span className={`text-sm font-medium ${isOverdue ? 'text-red-600' : 'text-gray-900'}`}>
                      {new Date(billing.dueDate).toLocaleDateString()}
                    </span>
                  </div>

                  {billing.paidDate && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Paid Date:</span>
                      <span className="text-sm font-medium text-green-600">
                        {new Date(billing.paidDate).toLocaleDateString()}
                      </span>
                    </div>
                  )}

                  {billing.paymentMethod && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Payment Method:</span>
                      <span className="text-sm font-medium text-gray-900">
                        {billing.paymentMethod.replace('_', ' ')}
                      </span>
                    </div>
                  )}

                  {billing.transactionId && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Transaction ID:</span>
                      <span className="text-sm font-mono text-gray-900">{billing.transactionId}</span>
                    </div>
                  )}

                  {isOverdue && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-center text-red-700">
                        <AlertTriangleIcon className="h-4 w-4 mr-2" />
                        <span className="text-sm font-medium">
                          {Math.ceil((new Date().getTime() - new Date(billing.dueDate).getTime()) / (1000 * 60 * 60 * 24))} days overdue
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Invoice Dates */}
              <div className="bg-white border border-gray-200 p-4 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Invoice Dates</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Created:</span>
                    <span className="text-sm text-gray-900">
                      {new Date(billing.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Due:</span>
                    <span className="text-sm text-gray-900">
                      {new Date(billing.dueDate).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              {canPayment && onPayment && (
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Payment Actions</h3>
                  <button
                    onClick={onPayment}
                    className="btn-primary w-full"
                  >
                    <CreditCardIcon className="h-4 w-4 mr-2" />
                    Process Payment
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingDetailsModal; 